import { SESSION_RECORDING_BLOCKED_CLASS } from "@medimo/chat/session-recording";
import type { CapturedNetworkRequest, PostHogConfig } from "posthog-js";
import { useLayoutEffect, useSyncExternalStore } from "react";

// 診察画面には患者情報が出るため、録画は AI チャットとその設定画面に限る。
const recordedPaths = [/^\/chat(?:\/|$)/, /^\/settings\/chat(?:\/|$)/];

export function isSessionRecordingPath(pathname: string): boolean {
  return recordedPaths.some((path) => path.test(pathname));
}

// 録画対象のパスの外でも録画したい画面(診察画面の AI チャットのパネル)が、開いている間だけ録画を求める。
const recordingRequests = { count: 0, listeners: new Set<() => void>() };

function changeRecordingRequests(delta: number) {
  recordingRequests.count += delta;
  for (const listener of recordingRequests.listeners) listener();
}

function subscribeRecordingRequests(listener: () => void) {
  recordingRequests.listeners.add(listener);
  return () => {
    recordingRequests.listeners.delete(listener);
  };
}

export function useSessionRecordingRequested(): boolean {
  return useSyncExternalStore(
    subscribeRecordingRequests,
    () => recordingRequests.count > 0,
    () => false,
  );
}

// パネルを閉じる・画面を離れるときも、遷移先の描画が録画に入る前に止まるよう描画と同じタスクで求めを外す。
// 返すクラスは、録画を求めている間だけ患者情報の領域を録画から外すためのもの。PostHog はこのクラスの
// 領域のデッドクリックをヒートマップに記録しないため、録画しない間は付けない。
export function useRequestSessionRecording(enabled: boolean): string {
  useLayoutEffect(() => {
    if (!enabled) return;
    changeRecordingRequests(1);
    return () => changeRecordingRequests(-1);
  }, [enabled]);
  return enabled ? SESSION_RECORDING_BLOCKED_CLASS : "";
}

function dropRequestContents(
  request: CapturedNetworkRequest,
): CapturedNetworkRequest {
  return {
    ...request,
    requestHeaders: undefined,
    requestBody: undefined,
    responseHeaders: undefined,
    responseBody: undefined,
  };
}

export const sessionRecordingOptions: Partial<PostHogConfig> = {
  // 録画対象の画面に入ったとき、または録画を求める画面を開いたときだけ開始する。
  disable_session_recording: true,
  // チャットの質問・回答・入力には患者情報が含まれうるため、文字・入力・画像はすべてブラウザ内で伏せる。
  session_recording: {
    maskAllInputs: true,
    maskTextSelector: "*",
    blockSelector: "img, video, canvas, iframe",
    maskCapturedNetworkRequestFn: dropRequestContents,
  },
  // remote config で有効になっても、録画にコンソールログと通信記録を残さない。
  enable_recording_console_log: false,
  capture_performance: { network_timing: false },
};
